import { useCallback, useRef, useState } from 'react'
import { DashboardCell, DashboardConfig, DashboardLayout, DashboardPage } from '../types/gotypes'
import { activePageId, resolvePageLayout } from '../utils/dashboard'
import {
  MultiTableRef,
  cloneCellForDuplicate,
  clonePageForDuplicate,
  cloneRowForDuplicate,
  collectMultiTableRefs,
  collectMultiTableRefsInRows,
  remapMultiTableIds,
  remapMultiTableIdsInRows,
  stripCellWidth,
} from '../utils/dashboardDuplicate'
import { CellRef, insertWidgetAtDropTargetWithCell } from '../utils/dashboardBuilderLayout'

export interface UseDashboardDuplicateProps {
  /** Reads the LIVE editor config. Never a captured value — see the note below. */
  getConfig: () => DashboardConfig
  /** Reads the LIVE active page index. */
  getActivePageIndex: () => number
  /** Commits a whole config (used by tab duplication, which adds a page). */
  commitConfig: (config: DashboardConfig) => void
  /** Commits a layout onto the page `pageId` names — the page the user clicked
   *  duplicate on, not whichever page is active when the fork finishes.
   *  `undefined` means a legacy dashboard with no pages. */
  commitLayout: (layout: DashboardLayout, pageId: string | undefined) => void
  /** Forks the multi-tables a duplicated node references; returns old id -> new id. */
  forkMultiTables: (refs: MultiTableRef[]) => Promise<Record<string, string>>
  /** Reports a duplicate that could not land (its destination was deleted mid-fork). */
  onDropped: (reason: string) => void
  /** Called with the index of a freshly duplicated page so the editor can select it. */
  onPageDuplicated?: (pageIndex: number) => void
}

/** The destination no longer exists. Never drop a duplicate silently — the user
 *  clicked, waited, and is owed an answer. */
const PAGE_GONE = 'That page was deleted while the copy was preparing, so nothing was copied.'
const ROW_GONE = 'That row was deleted while the copy was preparing, so nothing was copied.'
const COLUMN_GONE = 'That column was deleted while the copy was preparing, so nothing was copied.'

/**
 * Duplication in the dashboard editor — the whole of what "copy" now means
 * (see utils/dashboardDuplicate.ts for why the clipboard went away).
 *
 * Every duplicate forks the multi-table definitions the copied node references,
 * and a fork is one HTTP round-trip per referenced analysis — duplicating a tab
 * with five analysis cards waits on ten sequential requests. Three consequences
 * drive the whole shape of this hook, and are unchanged from the paste flow it
 * replaces:
 *
 *  1. **Nothing may be read from a render closure across that await.** The user
 *     keeps editing while the fork runs (text edits, new rows, switching page),
 *     and a commit built on the pre-fork config would overwrite those edits and
 *     then persist the overwrite through the editor's debounced save. So the
 *     config and the destination row are re-read through `getConfig` AFTER the
 *     fork resolves.
 *
 *  2. **The destination page is captured at click time, by id.** The user may
 *     switch tab while the fork runs, and re-reading the active page index then
 *     would land the copy on a page they never touched. An id, not an index,
 *     because pages can also be reordered or deleted meanwhile. If the captured
 *     page is gone at commit time the duplicate is cancelled and reported.
 *
 *  3. **One duplicate at a time.** A second click during the wait would fork a
 *     second set of multi-tables (orphaning the first) and commit a second time
 *     from a stale base. `isDuplicating` is a ref-backed, synchronous gate
 *     covering every entry point.
 */
export function useDashboardDuplicate(props: UseDashboardDuplicateProps) {
  // The deps are read through a ref so the returned handlers stay referentially
  // stable AND always see the current callbacks, including after an await
  // inside a handler that started several renders ago.
  const propsRef = useRef(props)
  propsRef.current = props

  const [isDuplicating, setIsDuplicating] = useState(false)
  const duplicatingRef = useRef(false)

  /** The page this duplicate is aimed at. Call at click time, never after an await. */
  const capturePage = useCallback(
    (): string | undefined => activePageId(propsRef.current.getConfig(), propsRef.current.getActivePageIndex()),
    []
  )

  /** The captured page's CURRENT layout, or null if it has since been deleted. */
  const liveLayoutOf = useCallback(
    (pageId: string | undefined): DashboardLayout | null => resolvePageLayout(propsRef.current.getConfig(), pageId),
    []
  )

  const run = useCallback(async (task: () => Promise<void>) => {
    // Ref, not state: two clicks in the same tick must not both get through.
    if (duplicatingRef.current) return
    duplicatingRef.current = true
    setIsDuplicating(true)
    try {
      await task()
    } finally {
      duplicatingRef.current = false
      setIsDuplicating(false)
    }
  }, [])

  /**
   * Duplicate a top-level widget into the next slot: immediately after itself
   * in the same row when the row has room, otherwise a new row directly below.
   * The user picks nothing and can be refused nothing — that IS the feature.
   */
  const duplicateWidget = useCallback(
    (ref: CellRef) =>
      run(async () => {
        const { forkMultiTables, commitLayout, onDropped } = propsRef.current
        const pageId = capturePage()
        const source = liveLayoutOf(pageId)?.rows[ref.rowIndex]?.cells[ref.cellIndex]
        if (!source) return
        const cloned = cloneCellForDuplicate(source)
        const mapping = await forkMultiTables(collectMultiTableRefs([cloned]))

        const layout = liveLayoutOf(pageId)
        if (!layout) {
          onDropped(PAGE_GONE)
          return
        }
        if (!layout.rows[ref.rowIndex]) {
          onDropped(ROW_GONE)
          return
        }
        // The copy drops its source width so the row it lands in splits evenly
        // (a 70%-wide widget copied beside one other would otherwise be 70/30).
        const [copy] = remapMultiTableIds([stripCellWidth(cloned)], mapping)
        commitLayout(
          insertWidgetAtDropTargetWithCell(
            layout,
            { kind: 'cell-gap', rowIndex: ref.rowIndex, index: ref.cellIndex + 1 },
            copy
          ),
          pageId
        )
      }),
    [capturePage, liveLayoutOf, run]
  )

  /** Duplicate a widget inside a nested `layout` column, directly below itself. */
  const duplicateInnerWidget = useCallback(
    (rowIndex: number, cellIndex: number, innerRowIndex: number, innerCellIndex: number) =>
      run(async () => {
        const { forkMultiTables, commitLayout, onDropped } = propsRef.current
        const pageId = capturePage()
        const source = liveLayoutOf(pageId)?.rows[rowIndex]?.cells[cellIndex]
          ?.layout_data?.rows[innerRowIndex]?.cells[innerCellIndex]
        if (!source) return
        const cloned = cloneCellForDuplicate(source)
        const mapping = await forkMultiTables(collectMultiTableRefs([cloned]))

        const layout = liveLayoutOf(pageId)
        if (!layout) {
          onDropped(PAGE_GONE)
          return
        }
        const outerCell = layout.rows[rowIndex]?.cells[cellIndex]
        if (!outerCell?.layout_data) {
          onDropped(COLUMN_GONE)
          return
        }
        // A column's inner rows are full width, so a width carried over from the
        // outer row would render the copy narrow with dead space beside it.
        const [copy] = remapMultiTableIds([stripCellWidth(cloned)], mapping)
        const innerRows = [...outerCell.layout_data.rows]
        innerRows.splice(clampIndex(innerRowIndex + 1, innerRows.length), 0, { cells: [copy] })
        const updatedCell: DashboardCell = {
          ...outerCell,
          layout_data: { ...outerCell.layout_data, rows: innerRows },
        }
        const rows = layout.rows.map((row, i) => {
          if (i !== rowIndex) return row
          return { ...row, cells: row.cells.map((c, j) => (j === cellIndex ? updatedCell : c)) }
        })
        commitLayout({ ...layout, rows }, pageId)
      }),
    [capturePage, liveLayoutOf, run]
  )

  /** Duplicate a whole row directly below itself. */
  const duplicateRow = useCallback(
    (rowIndex: number) =>
      run(async () => {
        const { forkMultiTables, commitLayout, onDropped } = propsRef.current
        const pageId = capturePage()
        const source = liveLayoutOf(pageId)?.rows[rowIndex]
        if (!source) return
        // A whole row keeps its cells' widths: they are already consistent with
        // each other, and the copy lands as its own row rather than joining one.
        const cloned = cloneRowForDuplicate(source)
        const mapping = await forkMultiTables(collectMultiTableRefs(cloned.cells))

        const layout = liveLayoutOf(pageId)
        if (!layout) {
          onDropped(PAGE_GONE)
          return
        }
        const [copy] = remapMultiTableIdsInRows([cloned], mapping)
        const rows = [...layout.rows]
        rows.splice(clampIndex(rowIndex + 1, rows.length), 0, copy)
        commitLayout({ ...layout, rows }, pageId)
      }),
    [capturePage, liveLayoutOf, run]
  )

  /** Duplicate a tab directly after itself. */
  const duplicatePage = useCallback(
    (pageIndex: number) =>
      run(async () => {
        const { forkMultiTables, commitConfig, onDropped, onPageDuplicated } = propsRef.current
        // The copy lands after the page the user clicked, so that page is
        // captured by id too — an index would point at a different page if the
        // list changed while we forked.
        const anchorPage = (propsRef.current.getConfig().pages || [])[pageIndex]
        if (!anchorPage) return
        const cloned = clonePageForDuplicate(anchorPage)
        const mapping = await forkMultiTables(collectMultiTableRefsInRows(cloned.layout.rows))

        const copy: DashboardPage = {
          ...cloned,
          layout: { ...cloned.layout, rows: remapMultiTableIdsInRows(cloned.layout.rows, mapping) },
        }
        const config = propsRef.current.getConfig()
        const pages = [...(config.pages || [])]
        const anchorIndex = pages.findIndex(p => p.id === anchorPage.id)
        if (anchorIndex < 0) {
          onDropped(PAGE_GONE)
          return
        }
        const at = anchorIndex + 1
        pages.splice(at, 0, copy)
        commitConfig({ ...config, pages })
        onPageDuplicated?.(at)
      }),
    [run]
  )

  return {
    /** True while a duplicate is forking multi-tables. Every duplicate control
     *  must disable on this, or a second click double-forks and commits stale. */
    isDuplicating,
    duplicateWidget,
    duplicateInnerWidget,
    duplicateRow,
    duplicatePage,
  }
}

/** Keep an insertion point inside a list that may have changed during the fork. */
function clampIndex(index: number, length: number): number {
  return Math.min(Math.max(index, 0), length)
}

export default useDashboardDuplicate
